import React, { useMemo, useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userData } = useAuth();
  const sourcePath = useMemo(() => searchParams.get('from') || '/', [searchParams]);

  const [category, setCategory] = useState<'feedback' | 'feature'>('feedback');
  const [email, setEmail] = useState(user?.email || '');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = title.trim().length > 2 && message.trim().length > 8;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, 'feedback_messages'), {
        category,
        title: title.trim(),
        message: message.trim(),
        email: email.trim() || null,
        sourcePath,
        status: 'new',
        createdAt: serverTimestamp(),
        userId: user?.uid || null,
        userDisplayName: userData?.displayName || user?.displayName || null,
      });
      setSubmitted(true);
      setTitle('');
      setMessage('');
    } catch (error) {
      console.error('Failed to submit feedback', error);
      alert('Failed to send feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f5f5f4_0%,#fafaf9_55%,#ffffff_100%)] px-4 py-10 text-stone-900">
      <div className="mx-auto max-w-3xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 text-sm font-medium text-stone-600 transition-colors hover:text-stone-900"
        >
          ← Back
        </button>

        <div className="rounded-[32px] border border-stone-200 bg-white p-8 shadow-[0_30px_80px_rgba(41,37,36,0.08)]">
          <h1 className="text-3xl font-bold tracking-tight text-stone-950">Give feedback or request a feature</h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-stone-600">
            If something feels unclear, missing, or worth improving, send it here and it will land in the admin message queue.
          </p>

          <div className="mt-5 rounded-2xl bg-stone-50 px-4 py-3 text-sm text-stone-600 ring-1 ring-stone-200">
            Source page: <span className="font-medium text-stone-900">{sourcePath}</span>
          </div>

          {submitted ? (
            <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-900">
              <h2 className="text-lg font-semibold">Message received</h2>
              <p className="mt-2 text-sm text-emerald-800">
                Your note is in the admin inbox now. You can send another one or return to the app.
              </p>
              <div className="mt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="rounded-xl bg-emerald-900 px-4 py-2 text-sm font-semibold text-white"
                >
                  Send another
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="rounded-xl border border-emerald-300 bg-white px-4 py-2 text-sm font-semibold text-emerald-900"
                >
                  Go home
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setCategory('feedback')}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    category === 'feedback'
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  General feedback
                </button>
                <button
                  type="button"
                  onClick={() => setCategory('feature')}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    category === 'feature'
                      ? 'bg-stone-900 text-white'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  Feature request
                </button>
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-800">Email for follow-up</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Optional"
                  className="w-full rounded-2xl border border-stone-300 px-4 py-3 text-stone-900 outline-none transition-colors focus:border-stone-900"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-800">Short title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={category === 'feature' ? 'Example: Bring back host-side song notes' : 'Example: Search page feels too bare'}
                  className="w-full rounded-2xl border border-stone-300 px-4 py-3 text-stone-900 outline-none transition-colors focus:border-stone-900"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-stone-800">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={7}
                  placeholder="What were you trying to do, and what would make the lean experience better?"
                  className="w-full rounded-2xl border border-stone-300 px-4 py-3 text-stone-900 outline-none transition-colors focus:border-stone-900"
                />
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="rounded-2xl bg-stone-900 px-5 py-3 text-sm font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {isSubmitting ? 'Sending...' : 'Send to admin inbox'}
                </button>
                <p className="text-xs text-stone-500">Visible to admins only after submission.</p>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackPage;